import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, ArrowRight, User, Tag } from 'lucide-react';

const blogPosts = [
  {
    id: 1,
    title: "Winter 2025-2026 Fire Watch Readiness: Protecting Properties Nationwide",
    excerpt: "As winter approaches, property managers and business owners need to prepare for potential fire watch requirements. Learn how to stay compliant and protect your property during the cold season.",
    image: "https://images.unsplash.com/photo-1723399726195-1fc25450f80d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMHBhdHJvbHxlbnwwfHx8fDE3Njg4OTI4MjJ8MA&ixlib=rb-4.1.0&q=85",
    date: "November 27, 2025",
    author: "Michael Roberts",
    category: "Fire Watch",
    readTime: "5 min read"
  },
  {
    id: 2,
    title: "Event Security Contract Costs: How Major Venues Budget Per Event",
    excerpt: "Seven hours before doors open, the command center is already buzzing. Learn what goes into planning and budgeting for large-scale event security operations.",
    image: "https://images.unsplash.com/photo-1552739883-6578dc150ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxhcm1lZCUyMGd1YXJkfGVufDB8fHx8MTc2ODg5MjgzNHww&ixlib=rb-4.1.0&q=85",
    date: "November 22, 2025",
    author: "Sarah Chen",
    category: "Event Security",
    readTime: "8 min read"
  },
  {
    id: 3,
    title: "Executive Protection Pricing: What Individuals Pay for 24/7 Security",
    excerpt: "The math of staying safe: Understanding the costs and considerations involved in hiring personal protection services for high-profile individuals and executives.",
    image: "https://images.pexels.com/photos/13569269/pexels-photo-13569269.jpeg",
    date: "November 21, 2025",
    author: "James Mitchell",
    category: "Executive Protection",
    readTime: "6 min read"
  },
  {
    id: 4,
    title: "Construction Site Security: Preventing Theft and Vandalism",
    excerpt: "Construction sites are prime targets for theft and vandalism. Learn effective strategies to protect your equipment, materials, and work-in-progress.",
    image: "https://images.pexels.com/photos/27299956/pexels-photo-27299956.jpeg",
    date: "November 18, 2025",
    author: "David Thompson",
    category: "Commercial Security",
    readTime: "7 min read"
  },
  {
    id: 5,
    title: "The Role of Technology in Modern Security Operations",
    excerpt: "From AI-powered surveillance to mobile patrol apps, discover how technology is transforming the security industry and improving protection services.",
    image: "https://images.unsplash.com/photo-1765121689322-6befc57dc8db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMHBhdHJvbHxlbnwwfHx8fDE3Njg4OTI4MjJ8MA&ixlib=rb-4.1.0&q=85",
    date: "November 15, 2025",
    author: "Emily Watson",
    category: "Industry Trends",
    readTime: "5 min read"
  },
  {
    id: 6,
    title: "Retail Loss Prevention: Strategies That Actually Work",
    excerpt: "Shoplifting and employee theft cost retailers billions annually. Explore proven strategies and security measures to protect your retail business.",
    image: "https://images.pexels.com/photos/13657406/pexels-photo-13657406.jpeg",
    date: "November 12, 2025",
    author: "Robert Kim",
    category: "Loss Prevention",
    readTime: "6 min read"
  }
];

const categories = ["All", "Fire Watch", "Event Security", "Executive Protection", "Commercial Security", "Loss Prevention", "Industry Trends"];

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = React.useState("All");

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <>
      <SEO 
        title="Security Blog"
        description="Read the latest articles and insights about security services, security guard tips, industry news, and best practices from Armed Security Service."
        keywords="security blog, security articles, security tips, security industry news, security guard information"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Security <span className="text-orange-500">Blog</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Stay informed with the latest security industry news, tips, and insights from our team of experts.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full font-medium text-sm transition-all ${
                  selectedCategory === category
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-800 text-gray-400 hover:bg-slate-700 hover:text-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Post */}
          {filteredPosts.length > 0 && (
            <div className="mb-12">
              <Link to={`/blog/${filteredPosts[0].id}`} className="group block">
                <div className="grid lg:grid-cols-2 gap-8 bg-slate-900 rounded-2xl overflow-hidden">
                  <div className="relative h-64 lg:h-auto">
                    <img 
                      src={filteredPosts[0].image} 
                      alt={filteredPosts[0].title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <span className="inline-block bg-orange-500/20 text-orange-500 px-3 py-1 rounded-full text-xs font-medium mb-4 w-fit">
                      {filteredPosts[0].category}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-orange-400 transition-colors">
                      {filteredPosts[0].title}
                    </h2>
                    <p className="text-gray-400 mb-6 line-clamp-3">{filteredPosts[0].excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {filteredPosts[0].author}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {filteredPosts[0].date}
                      </span>
                      <span>{filteredPosts[0].readTime}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Other Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.slice(1).map((post) => (
              <Link 
                key={post.id}
                to={`/blog/${post.id}`}
                className="group bg-slate-900 rounded-xl overflow-hidden hover:shadow-2xl transition-all"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <span className="absolute top-4 left-4 bg-orange-500/90 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {post.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-3 line-clamp-2 group-hover:text-orange-400 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Stay Updated</h2>
          <p className="text-gray-400 mb-8">
            Subscribe to our newsletter for the latest security tips, industry news, and company updates.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
            />
            <button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default BlogPage;